import { FunctionComponent } from 'react';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { AllCyclesResponse } from '@/api/dashboard/types';
import { formatNumber } from '@/features/dashboard/utils';

interface AllCyclesChartTitleProps {
	allCycles?: AllCyclesResponse;
	sx?: SxProps<Theme>;
	colors: string[];
}

const AllCyclesChartTitle: FunctionComponent<AllCyclesChartTitleProps> = ({
	allCycles,
	sx,
	colors,
}) => (
	<Stack
		direction="row"
		spacing={2}
		sx={{
			...sx,
			overflowX: 'auto',
			overflowY: 'hidden',
			width: '100%',
			flexWrap: 'nowrap',
			'@media print': {
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
				overflow: 'visible',
				flexWrap: 'wrap',
				gap: 2,
			},
		}}
	>
		{allCycles?.data?.map((record, idx) => {
			const total = record.points.reduce((sum, p) => sum + (p.count || 0), 0);
			if (!record.site) return null;
			return (
				<Box
					key={record.site.id}
					display="flex"
					alignItems="center"
					gap={1}
					sx={{
						minWidth: 120,
						maxWidth: 180,
						flex: '0 0 180px',
						paddingBottom: 1,
						'@media print': {
							paddingLeft: idx === 0 ? 2 : 0,
							breakInside: 'avoid',
						},
					}}
				>
					<Stack
						direction="column"
						spacing={1}
						alignItems="left"
						sx={{
							borderLeft: idx > 0 ? 2 : 0,
							pl: idx > 0 ? 2 : 0,
							borderColor: 'divider',
							'@media print': {
								borderLeft: 2,
								pl: 2,
								borderColor: 'divider',
							},
						}}
					>
						<Typography
							variant="h3"
							sx={{
								wordBreak: 'break-word',
								whiteSpace: 'normal',
								textAlign: 'left',
								maxWidth: '100%',
							}}
						>
							{formatNumber(total)}
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center">
							<Box
								sx={{
									width: 40,
									height: 40,
									bgcolor: colors[idx % colors.length],
									flexShrink: 0,
									WebkitPrintColorAdjust: 'exact',
									printColorAdjust: 'exact',
								}}
							/>
							<Typography
								variant="body2"
								sx={{
									wordBreak: 'break-word',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									whiteSpace: 'normal',
									maxWidth: 140,
									fontSize: '12px',
								}}
							>
								{record.site.name}
							</Typography>
						</Stack>
					</Stack>
				</Box>
			);
		})}
	</Stack>
);

export default AllCyclesChartTitle;
